import React, { useState } from 'react';
import { X, Layers, AlertTriangle, Package, Scale, CheckCircle2, Loader2 } from 'lucide-react';
import { pickingService } from '../../../../../services/pickingService';
import { ValidateOperatorBatchResponse } from '../../../../../types/picking';

interface OperatorBatchConfirmModalProps {
  isOpen: boolean;
  validation: ValidateOperatorBatchResponse | null;
  selectedPListIds: number[];
  onClose: () => void;
  onBatchCreated: (newPListId: number) => void;
}

export const OperatorBatchConfirmModal: React.FC<OperatorBatchConfirmModalProps> = ({
  isOpen,
  validation,
  selectedPListIds,
  onClose,
  onBatchCreated
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ newPListId: number; totalLines: number; totalPackages: number } | null>(null);

  if (!isOpen) return null;

  const handleCreate = async () => {
    setIsCreating(true);
    setError(null);
    try {
      const result = await pickingService.createOperatorBatch(selectedPListIds);
      if (result.success) {
        setSuccess({
          newPListId: result.newPListId,
          totalLines: result.totalLines,
          totalPackages: result.totalPackages
        });
        // Auto-navigate after a short delay
        setTimeout(() => {
          onBatchCreated(result.newPListId);
        }, 1500);
      } else {
        setError(result.errorMessage || 'Ukjent feil ved opprettelse.');
      }
    } catch (err: any) {
      setError(err.message || 'Feil ved opprettelse av samleplukk.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setError(null);
      setSuccess(null);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-2xl sm:max-h-[90vh] flex flex-col overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-orange-50">
          <div className="flex items-center gap-2">
            <Layers className="text-[#F58220]" size={22} />
            <h2 className="text-lg font-bold text-gray-900">Bekreft samleplukk</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isCreating}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Success state */}
          {success && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CheckCircle2 className="text-green-500 mb-3" size={48} />
              <h3 className="text-lg font-bold text-gray-900 mb-1">Samleplukk opprettet!</h3>
              <p className="text-sm text-gray-600">
                Plukkliste #{success.newPListId} med {success.totalLines} linjer og {success.totalPackages} pakker.
              </p>
              <p className="text-xs text-gray-400 mt-2">Navigerer til plukklisten...</p>
            </div>
          )}

          {/* Normal state */}
          {!success && (
            <>
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Sammendrag</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#F58220]">{selectedPListIds.length}</div>
                    <div className="text-xs text-gray-500">Plukklister</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{validation?.totalLines ?? '-'}</div>
                    <div className="text-xs text-gray-500">Linjer totalt</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {validation?.totalWeight ? `${validation.totalWeight.toFixed(1)}` : '-'}
                    </div>
                    <div className="text-xs text-gray-500">Vekt (kg)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900">
                      {validation?.totalVolume ? `${validation.totalVolume.toFixed(2)}` : '-'}
                    </div>
                    <div className="text-xs text-gray-500">Volum (m3)</div>
                  </div>
                </div>
              </div>

              {/* Source PLists */}
              {validation?.sourcePLists && validation.sourcePLists.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Plukklister som slås sammen</h3>
                  <div className="space-y-2">
                    {validation.sourcePLists.map(pl => (
                      <div key={pl.pListID} className="flex items-center justify-between bg-white border rounded-lg p-3">
                        <div className="flex items-center gap-3">
                          <Package size={16} className="text-gray-400" />
                          <div>
                            <span className="font-medium text-sm">#{pl.pListID}</span>
                            {pl.description1 && (
                              <span className="text-xs text-gray-500 ml-2">{pl.description1}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{pl.numOfOrderLines ?? 0} linjer</span>
                          {pl.weight != null && pl.weight > 0 && (
                            <span className="flex items-center gap-1">
                              <Scale size={12} />
                              {pl.weight.toFixed(1)} kg
                            </span>
                          )}
                          <span>P:{pl.priority}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Warnings */}
              {validation?.warnings && validation.warnings.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="text-amber-600" size={18} />
                    <span className="font-medium text-amber-800 text-sm">Advarsler</span>
                  </div>
                  <ul className="space-y-1">
                    {validation.warnings.map((w, i) => (
                      <li key={i} className="text-sm text-amber-700">{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Errors */}
              {validation?.errors && validation.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="text-red-600" size={18} />
                    <span className="font-medium text-red-800 text-sm">Feil</span>
                  </div>
                  <ul className="space-y-1">
                    {validation.errors.map((e, i) => (
                      <li key={i} className="text-sm text-red-700">{e}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* In-modal error from creation attempt */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="text-red-600" size={18} />
                    <span className="text-sm text-red-700">{error}</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50">
            <button
              onClick={handleClose}
              disabled={isCreating}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 min-h-[44px]"
            >
              Avbryt
            </button>
            <button
              onClick={handleCreate}
              disabled={isCreating || (validation !== null && !validation.isValid)}
              className="flex items-center gap-2 px-6 py-2 bg-[#F58220] text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
            >
              {isCreating ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Oppretter...
                </>
              ) : (
                <>
                  <Layers size={18} />
                  Opprett samleplukk
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
